// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistorySearchV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4
//
// Search input for the history page. Client-side only — feeds the parent's
// `searchQuery` state, which the page filters in-memory across loaded threads
// (locked product decision: no `?q=` server param exists).
//
// ⌘K / Ctrl+K focuses the input from anywhere on the page (desktop only,
// suppressed on phone where the keyboard hint isn't shown).
import React, { useEffect, useRef } from "react";
import { Search, X } from "lucide-react";

interface MidasHistorySearchV2Props {
  value: string;
  onChange: (next: string) => void;
  /** Whether to show the "⌘K" hint pill (desktop only). */
  showKeyboardHint: boolean;
  placeholder: string;
  ariaLabel: string;
  clearLabel: string;
}

const MidasHistorySearchV2: React.FC<MidasHistorySearchV2Props> = ({
  value,
  onChange,
  showKeyboardHint,
  placeholder,
  ariaLabel,
  clearLabel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // ⌘K / Ctrl+K → focus
  useEffect(() => {
    if (!showKeyboardHint) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showKeyboardHint]);

  return (
    <label
      htmlFor="midas-history-search"
      className="flex h-12 w-full items-center gap-2.5 rounded-lg border border-[rgba(44,38,32,0.06)] bg-[#F4EFE3] px-4 transition-colors duration-150 focus-within:border-midas-terracotta focus-within:ring-2 focus-within:ring-[rgba(212,87,43,0.18)] motion-reduce:transition-none"
    >
      <Search
        className="h-4 w-4 flex-shrink-0 text-midas-muted-stone"
        strokeWidth={1.75}
        aria-hidden="true"
      />
      <input
        id="midas-history-search"
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        aria-label={ariaLabel}
        // Disable browser default outline ring; we use focus-within on the wrapper
        className="min-w-0 flex-1 bg-transparent font-sora text-[14px] text-midas-graphite placeholder:text-midas-muted-stone focus:outline-none"
        style={{ fontFamily: "'Sora', sans-serif" }}
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onChange("");
            inputRef.current?.focus();
          }}
          aria-label={clearLabel}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-midas-muted-stone outline-none transition-colors duration-150 hover:bg-[rgba(44,38,32,0.06)] hover:text-midas-warm-graphite focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}
      {showKeyboardHint && !value && (
        <span
          aria-hidden="true"
          className="flex-shrink-0 rounded-sm bg-[rgba(44,38,32,0.05)] px-1.5 py-0.5 font-mono text-[10px] font-medium text-midas-muted-stone"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          ⌘K
        </span>
      )}
    </label>
  );
};

export default MidasHistorySearchV2;
